import React, { useContext } from 'react';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import { useGmailTabsStyles, useGmailTabItemStyles } from '@mui-treasury/styles/tabs/gmail';
import { Tooltip, useMediaQuery, useTheme } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { useIntl } from 'react-intl';
import { useButtonColors, COUNT_COLOR, DARK_ACTION_BUTTON_COLOR } from '../../components/Buttons/ButtonConstants';
import { outlinedChipStyle, variantForColor } from '../../components/CustomChip/chipStyles';
import { ThemeModeContext } from '../../contexts/ThemeModeContext';
import { preventDefaultAndProp } from '../../utils/marketIdPathFunctions';

const useTabHoverStyles = makeStyles(({ palette }) => ({
  tabRoot: {
    '@media (hover: hover)': {
      '&:hover': {
        backgroundColor: palette.type === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(45,128,237,0.12)',
      },
    },
    '@media (hover: none)': {
      '&:hover': {
        backgroundColor: 'unset !important',
      },
    },
  },
}));

// B-all-475: the treasury gmail style insets the indicator line 8px on each side
// (margin: '0 8px' on the child div), so the blue line stopped short of the
// hover background, which covers the full tab. Span the whole tab instead.
const useIndicatorStyles = makeStyles({
  indicator: {
    '& > div': {
      margin: 0,
    },
  },
});

export function GmailTabItem(props) {
  const { color='#2F80ED', label, tag, tagLabel, hasChip=true, tagColor=COUNT_COLOR, toolTipId, icon,
    onTagClick, tagTooltipId, ...other } = props;
  const [themeMode] = useContext(ThemeModeContext);
  const isDark = themeMode === 'dark';
  const theme = useTheme();
  const intl = useIntl();
  const mobileLayout = useMediaQuery(theme.breakpoints.down('sm'));
  const tabItemStyles = useGmailTabItemStyles({ ...props, color });
  const hoverStyles = useTabHoverStyles();
  const useTagLabel = mobileLayout ? '' : (tagLabel || 'total');
  const useLabel = mobileLayout ? '' : (toolTipId ? <Tooltip
    title={intl.formatMessage({ id: toolTipId })}><div>{label}</div></Tooltip> : label);
  const defaultColor = isDark ? '#ffffff' : '#000000';
  const tagNode = (
    <span className={'MuiTabItem-tag'} onClick={onTagClick ? (event) => {
      preventDefaultAndProp(event);
      onTagClick();
    } : undefined} style={{
      ...(hasChip ? outlinedChipStyle(variantForColor(tagColor), isDark)
        : {backgroundColor: 'unset', color: defaultColor}),
      borderRadius: 22, paddingLeft: '7px', paddingRight: '7px',
      marginLeft: mobileLayout ? '-8px' : undefined,
      cursor: onTagClick ? 'pointer' : undefined}}>
      {tag} {useTagLabel}
    </span>
  );
  return (
    <Tab
      disableTouchRipple
      classes={{ ...tabItemStyles, root: `${tabItemStyles.root} ${hoverStyles.tabRoot}` }}
      {...other}
      icon={isDark && icon ? React.cloneElement(icon, { htmlColor: DARK_ACTION_BUTTON_COLOR }) : icon}
      id={label.replace(/[ &/]/g, '')}
      style={{maxWidth: '16rem', width: '12rem'}}
      label={
        <div className={'MuiTabItem-label'} style={{color: defaultColor, opacity: 0.6}}>
          {useLabel} {tag && (onTagClick ? (
            <Tooltip title={intl.formatMessage({ id: tagTooltipId || 'notificationGoTo' })}>{tagNode}</Tooltip>
          ) : tagNode)}
        </div>
      }
    />
  );
}

export function GmailTabs(props) {
  const tabsStyles = useGmailTabsStyles({ ...props });
  const indicatorStyles = useIndicatorStyles();
  const tabsProps = {...props};
  const { removeBoxShadow, addPaddingLeft, addMarginLeft, useColor=true } = props;
  const { lighterBlueColor } = useButtonColors();
  delete tabsProps.indicatorColors;
  delete tabsProps.useColor;
  delete tabsProps.removeBoxShadow;
  delete tabsProps.addPaddingLeft;
  delete tabsProps.addMarginLeft;
  return (
    <Tabs
      {...tabsProps}
      classes={{ ...tabsStyles, indicator: `${tabsStyles.indicator} ${indicatorStyles.indicator}` }}
      style={{boxShadow: removeBoxShadow ? 'unset' : undefined, paddingLeft: addPaddingLeft, marginLeft: addMarginLeft,
        backgroundColor: useColor ? lighterBlueColor : undefined }}
      TabIndicatorProps={{
        ...props.TabIndicatorProps,
        children: <div className={`MuiIndicator-${props.value}`} />,
      }}
    />
  );
}